import type { MouseEventHandler } from 'react';
import React, { useCallback } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { Box } from '@atlaskit/primitives/compiled/box';
import { updatePageRovoParams } from '@atlaskit/rovo-triggers';

import { preloadChatOnIntent } from '@atlassian/conversation-assistant/preload-chat-on-intent';
import { usePrefetchRecommendedSpacesOnOpenIntent } from '@atlassian/conversation-assistant/recommended-spaces-open-intent';
import { ConversationAssistantButtonNav4 } from '@atlassian/conversation-assistant-ui-components';
import { ChatContextSubscriber } from '@atlassian/conversation-assistant-store';
import {
	CONVERSATION_ASSISTANT_BRAND_NAME,
	ChatOpenerSubscriber,
	ConversationAssistantUIModes,
} from '@atlassian/conversation-assistant';

import { GeneralShortcutListener } from '@confluence/shortcuts/entry-points/GeneralShortcutListener.ts';
import {
	isMac,
	useShortcutStrings,
} from '@confluence/shortcuts/entry-points/ShortcutVisualizer.ts';
import {
	AI_MATE_SHORTCUT,
	UNIVERSAL_AI_MATE_SHORTCUT_MAC,
	UNIVERSAL_AI_MATE_SHORTCUT_WIN,
} from '@confluence/shortcuts/entry-points/confluence-shortcuts.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useLivePageMode } from '@confluence/live-pages-utils/entry-points/useLivePagesStore.ts';
import { useIsEditorPage } from '@confluence/route-manager/entry-points/useIsEditorPage.ts';

import { useToggleConversationAssistant } from './hooks/useToggleConversationAssistant.ts';

export const ConversationAssistantNavBarButton = (): React.JSX.Element => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { cloudId } = useSessionData();
	const [{ isEditMode: isLiveEditMode }] = useLivePageMode();
	const isOnEditRoute = useIsEditorPage();

	const { mode, toggleConversationAssistant, isConversationAssistantSidebarActive } =
		useToggleConversationAssistant();
	const prefetchRecommendedSpacesOnOpenIntent = usePrefetchRecommendedSpacesOnOpenIntent({
		siteId: cloudId,
	});

	const prefetchRecommendedSpacesIfChatWillOpen = useCallback(() => {
		if (isConversationAssistantSidebarActive || mode === ConversationAssistantUIModes.MINI_MODAL) {
			return;
		}

		prefetchRecommendedSpacesOnOpenIntent();
	}, [isConversationAssistantSidebarActive, mode, prefetchRecommendedSpacesOnOpenIntent]);

	const handleOnClick = useCallback<MouseEventHandler>(
		(e) => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'navigationItem',
					actionSubjectId: 'aiMate',
					source: 'AIMate',
					attributes: {
						opened: !isConversationAssistantSidebarActive,
						navigationLayer: 'global',
						navigationContainer: 'top',
						navVersion: '4',
					},
					tags: ['atlaskit', 'aiMate'],
				},
			}).fire();
			e.preventDefault();
			prefetchRecommendedSpacesIfChatWillOpen();
			toggleConversationAssistant();
		},
		[
			createAnalyticsEvent,
			isConversationAssistantSidebarActive,
			prefetchRecommendedSpacesIfChatWillOpen,
			toggleConversationAssistant,
		],
	);

	const handleMouseEnter = useCallback<MouseEventHandler>(() => {
		// Intent-time preload — FG-gated, once-only per page load, so safe on every hover.
		if (!isConversationAssistantSidebarActive) {
			preloadChatOnIntent();
		}
	}, [isConversationAssistantSidebarActive]);

	const universalAIMateShortcut = isMac()
		? UNIVERSAL_AI_MATE_SHORTCUT_MAC
		: UNIVERSAL_AI_MATE_SHORTCUT_WIN;

	const shortcutStrings = useShortcutStrings({ shortcut: universalAIMateShortcut });

	return (
		<Box as="span" paddingInlineStart="space.050" testId="app-navigation-ai-mate">
			<ConversationAssistantButtonNav4
				siteId={cloudId}
				productKey="confluence"
				enableOnboardingSpotlight
				onClick={handleOnClick}
				onMouseEnter={handleMouseEnter}
				isActive={isConversationAssistantSidebarActive}
				tooltipContent={CONVERSATION_ASSISTANT_BRAND_NAME}
				shortcut={
					// No shortcut on edit pages
					!isLiveEditMode && !isOnEditRoute ? shortcutStrings : undefined
				}
			/>
			<ChatContextSubscriber />
			<ChatOpenerSubscriber
				extensionContextBridgeEnabled
				onTriggerOpeningChat={(conversationId, openChatMode, _source, autoFocusInput) => {
					if (
						isConversationAssistantSidebarActive ||
						mode === ConversationAssistantUIModes.MINI_MODAL
					) {
						return;
					}

					conversationId &&
						updatePageRovoParams({
							pathway: 'chat',
							conversationId,
						});

					prefetchRecommendedSpacesOnOpenIntent();
					toggleConversationAssistant(openChatMode, autoFocusInput);
				}}
			/>
			<GeneralShortcutListener
				deprecatedAccelerator={!(isLiveEditMode || isOnEditRoute) ? AI_MATE_SHORTCUT : undefined}
				accelerator={universalAIMateShortcut}
				listener={() => {
					prefetchRecommendedSpacesIfChatWillOpen();
					toggleConversationAssistant('sidebar');
				}}
				isAppNavigationShortcut
			/>
		</Box>
	);
};
